import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Lock, X, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from '../store/toastStore';
import { isPushSupported, isPushSubscribed, subscribeToPushDetailed, requestPushPermissionNow, watchPushState, type PushSubscribeResult } from '../services/pushNotifications';
import { dismissUntilTomorrow, isDismissedToday, pushBarMode, pushFailKey, type PushBarMode } from '../utils/pushBannerModel';

/**
 * D-322 (6): a prominent bar at the top of the site for every OTE employee who has not approved browser notices on
 * THIS device - from the very first visit. "לא עכשיו" hides it until tomorrow only. Blocked (denied) browsers get the
 * same bar with the short "how to unblock" text.
 */
export default function PushBanner() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'admin' || role === 'agent';
  const isAdmin = role === 'admin';
  const [mode, setMode] = useState<PushBarMode>('hidden');
  // FIX-PUSH-REAL: the step that failed after "granted" (shown in the bar instead of asking to approve again).
  const [failStage, setFailStage] = useState<string | null>(null);
  const [failDetail, setFailDetail] = useState<string>('');
  const [working, setWorking] = useState(false);
  const failStageRef = useRef<string | null>(null);
  const autoTriedRef = useRef(false);

  const applyResult = (res: PushSubscribeResult): boolean => {
    if (res.ok) {
      failStageRef.current = null;
      setFailStage(null);
      setFailDetail('');
      setMode('hidden');
      return true;
    }
    if (Notification.permission === 'denied') { setMode('denied'); return false; }
    if (Notification.permission === 'granted') {
      const stage = res.reason === 'failed' ? (res.stage ?? 'unknown') : res.reason;
      failStageRef.current = stage;
      setFailStage(stage);
      setFailDetail(res.reason === 'failed' ? (res.detail ?? '') : '');
      setMode('failed');
      return false;
    }
    setMode('ask');
    return false;
  };

  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    let running = false;
    const check = async () => {
      if (running) return;
      running = true;
      try {
        const supported = isPushSupported();
        if (!supported) return;
        const dismissedToday = isDismissedToday(localStorage);
        if (dismissedToday) { if (!cancelled) setMode('hidden'); return; }
        let serverConfigured = false;
        try {
          // The manager asks the server whether it can really send (both keys, valid, switch on); others only need the public key.
          serverConfigured = isAdmin
            ? !!(await api.getPushStatus()).data?.ready
            : !!(await api.getVapidPublicKey()).data?.publicKey;
        } catch { /* no key = nothing to ask for */ }
        const permission = Notification.permission;
        let subscribed = permission === 'granted' ? await isPushSubscribed() : false;
        // FIX-PUSH-REAL: allowed already (here, in another window, or from the lock icon) but this browser is not
        // registered: register it now - no permission window is needed for that. Once per page load; then the button.
        if (permission === 'granted' && !subscribed && serverConfigured && !autoTriedRef.current) {
          autoTriedRef.current = true;
          const res = await subscribeToPushDetailed(Promise.resolve('granted'));
          if (cancelled) return;
          if (!res.ok) { applyResult(res); return; }
          subscribed = true;
        }
        if (cancelled) return;
        if (subscribed) { failStageRef.current = null; setFailStage(null); }
        setMode(pushBarMode({
          isStaff, supported, permission, subscribed, dismissedToday, serverConfigured, isAdmin,
          registerFailed: !subscribed && failStageRef.current !== null,
        }));
      } finally {
        running = false;
      }
    };
    void check().catch(() => { /* never block the app because of the bar */ });
    // FIX-PUSH-REAL: permission changed / back to this window / subscribed on another screen -> look again.
    const stop = watchPushState(() => { void check().catch(() => undefined); });
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, isAdmin]);

  // FIX-PUSH-REAL: the bar only ever SUBSCRIBES - no click on it unsubscribes this browser.
  const handleAllow = async () => {
    if (working) return;
    // The permission window opens straight from the click (subscribeToPushDetailed asks before any await).
    const pending = subscribeToPushDetailed();
    setWorking(true);
    try {
      applyResult(await pending);
    } finally {
      setWorking(false);
    }
  };

  // D-341: the manager creates the VAPID pair with one click (POST /push/enable; creates only when missing), then this device is asked to approve.
  const [enabling, setEnabling] = useState(false);
  const handleEnableServer = async () => {
    if (enabling) return;
    // FIX-BUG-CARD-PUSH: the browser's permission window opens straight from this click (before any await),
    // then the server keys are created, then THIS browser subscribes - one click, really on.
    const permissionAsked = requestPushPermissionNow();
    setEnabling(true);
    try {
      const r = await api.enableBrowserPush();
      if (r.data?.ready) {
        toast('success', t('push.enabledForAll'));
        const res = await subscribeToPushDetailed(permissionAsked);
        if (!res.ok) {
          applyResult(res);
        } else {
          const permission = Notification.permission;
          const subscribed = permission === 'granted' ? await isPushSubscribed() : false;
          setMode(pushBarMode({ isStaff, supported: true, permission, subscribed, dismissedToday: false, serverConfigured: true, isAdmin }));
        }
      } else {
        toast('error', t('push.enableFailed'));
      }
    } catch {
      toast('error', t('push.enableFailed'));
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = () => {
    dismissUntilTomorrow(localStorage);
    setMode('hidden');
  };

  if (mode === 'hidden') return null;
  const denied = mode === 'denied';
  const serverMissing = mode === 'server_missing';
  const registerFailed = mode === 'failed';

  // FIX-PUSH-REAL: allowed in the browser, but registering this browser failed - say what failed, offer a retry
  // (never "approve" again: the browser already approved). The retry only subscribes.
  if (registerFailed) {
    return (
      <div
        className="push-banner bg-warning-50 text-warning-900 border-b border-warning-200 px-4 py-3 flex items-start gap-3 flex-wrap"
        role="region"
        aria-label={t('push.barLabel')}
        data-testid="push-banner"
        data-mode={mode}
        data-stage={failStage ?? undefined}
      >
        <AlertTriangle size={22} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-[14rem]">
          <p className="text-sm font-semibold">{t('push.failTitle')}</p>
          <p className="text-sm mt-1" data-testid="push-fail-reason">{t(pushFailKey(failStage))}</p>
          {failDetail ? <p className="text-xs mt-1 opacity-80" dir="ltr" data-testid="push-fail-detail">{failDetail}</p> : null}
          <div className="flex gap-2 mt-2">
            <button onClick={() => void handleAllow()} disabled={working} className="btn btn-primary text-xs py-1.5 px-4" data-testid="push-banner-retry">
              {t('push.failRetry')}
            </button>
            <button onClick={handleDismiss} className="btn btn-secondary text-xs py-1.5 px-4" data-testid="push-banner-later">
              {t('push.askLater')}
            </button>
          </div>
        </div>
        <button onClick={handleDismiss} className="flex-shrink-0 opacity-70 hover:opacity-100" aria-label={t('push.askLater')}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="push-banner bg-warning-50 text-warning-900 border-b border-warning-200 px-4 py-3 flex items-start gap-3 flex-wrap"
      role="region"
      aria-label={t('push.barLabel')}
      data-testid="push-banner"
      data-mode={mode}
    >
      {denied ? <Lock size={22} className="flex-shrink-0 mt-0.5" aria-hidden="true" /> : <Bell size={22} className="flex-shrink-0 mt-0.5" aria-hidden="true" />}
      <div className="flex-1 min-w-[14rem]">
        <p className="text-sm font-semibold">{denied ? t('push.deniedTitle') : serverMissing ? t('push.serverMissingTitle') : t('push.barTitle')}</p>
        {serverMissing && <p className="text-sm mt-1" data-testid="push-server-missing">{t('push.serverMissingHow')}</p>}
        {denied && <p className="text-sm mt-1">{t('push.deniedHow')}</p>}
        {denied && (
          <svg viewBox="0 0 260 36" width="260" height="36" className="mt-2 max-w-full" role="img" aria-label={t('push.deniedImageAlt')}  data-testid="push-denied-illustration">
            <rect x="1" y="1" width="258" height="34" rx="17" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
            <rect x="14" y="9" width="18" height="18" rx="9" fill="currentColor" opacity="0.15" />
            <rect x="19" y="17" width="8" height="7" rx="1.5" fill="currentColor" />
            <path d="M20.5 17v-2.5a2.5 2.5 0 0 1 5 0V17" fill="none" stroke="currentColor" strokeWidth="1.5" />

            <text x="46" y="23" fontSize="12" fill="currentColor" opacity="0.8">ote-system.example</text>

          </svg>
        )}
        <div className="flex gap-2 mt-2">
          {serverMissing && (
            <button onClick={() => void handleEnableServer()} disabled={enabling} className="btn btn-primary text-xs py-1.5 px-4" data-testid="push-banner-enable-server">
              {t('push.enableForAll')}
            </button>
          )}
          {!denied && !serverMissing && (
            <button onClick={() => void handleAllow()} disabled={working} className="btn btn-primary text-xs py-1.5 px-4" data-testid="push-banner-allow">
              {t('push.allow')}
            </button>
          )}
          <button onClick={handleDismiss} className="btn btn-secondary text-xs py-1.5 px-4" data-testid="push-banner-later">
            {t('push.askLater')}
          </button>
        </div>
      </div>
      <button onClick={handleDismiss} className="flex-shrink-0 opacity-70 hover:opacity-100" aria-label={t('push.askLater')}>
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
